import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { signInWithEmailAndPassword, signOut, getIdToken } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, firestore } from "@/lib/firebase";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Spinner";

const schema = z.object({
  email: z.string().min(1, "Email is required.").email("Enter a valid email address."),
  password: z.string().min(1, "Password is required."),
});

type LoginFormValues = z.infer<typeof schema>;

export function LoginPage() {
  const user = useAdminAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (user) void navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Still resolving auth state — render nothing to avoid flashing the form
  if (user === undefined) return null;

  async function onSubmit({ email, password }: LoginFormValues) {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const fbUser = credential.user;

      const adminCheckPromise = getDoc(doc(firestore, "admin", fbUser.uid));
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 10_000)
      );

      const snap = await Promise.race([adminCheckPromise, timeoutPromise]);

      if (!snap.exists() || snap.data()?.["active"] !== true) {
        await signOut(auth);
        setError("root", { message: "Access denied. You are not an administrator." });
        return;
      }

      await getIdToken(fbUser);
      // Auth state change fires → ProtectedShell redirects to /dashboard
    } catch (err) {
      await signOut(auth).catch(() => {});

      if (err instanceof Error) {
        if (err.message === "timeout") {
          setError("root", { message: "Unable to verify admin permissions. Please try again." });
        } else if (
          err.message.includes("auth/invalid-credential") ||
          err.message.includes("auth/wrong-password") ||
          err.message.includes("auth/user-not-found")
        ) {
          setError("root", { message: "Invalid email or password." });
        } else if (err.message.includes("auth/too-many-requests")) {
          setError("root", { message: "Too many failed attempts. Please try again later." });
        } else if (err.message.includes("auth/network-request-failed")) {
          setError("root", { message: "Network error. Please check your connection." });
        } else {
          setError("root", { message: "Sign in failed. Please try again." });
        }
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo / title */}
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">
            DISCA<span className="text-primary"> Admin</span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your admin account</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              aria-invalid={!!errors.email}
              {...register("email")}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              {...register("password")}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          {/* Root / server error */}
          {errors.root && (
            <p className="text-sm text-destructive text-center">{errors.root.message}</p>
          )}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? <Spinner className="size-4 border-background" /> : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
